import {
  Controller,
  Control,
  FieldValues,
  DeepMap,
  FieldError,
  RegisterOptions,
} from 'react-hook-form';
import TextField from '@mui/material/TextField';
import { Tooltip } from '@mui/material';
import { getFormFieldError } from '../../helpers';
import type { ChangeEventHandler, FocusEventHandler } from 'react';

interface TextInputProps {
  label: string;
  name: string;
  control: Control<any>;
  required?: boolean;
  errors?: DeepMap<FieldValues, FieldError>;
  rules?: Exclude<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >;
  inputProps?: any;
  InputProps?: any;
  type?: string;
  defaultValue?: any;
  className?: string;
  multiline?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  tooltip?: string;
  tooltipPlacement?:
    | 'bottom'
    | 'bottom-end'
    | 'bottom-start'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
  children?: React.ReactNode;
  onBlur?:
    | FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  onChange?:
    | ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
}

export default function TextInput({
  label,
  name,
  control,
  required = false,
  errors,
  rules,
  inputProps = undefined,
  type = 'text',
  defaultValue = undefined,
  className = undefined,
  multiline = false,
  fullWidth = false,
  disabled = false,
  readOnly = false,
  tooltip = '',
  tooltipPlacement,
  children,
  InputProps,
  onBlur,
  onChange,
}: TextInputProps) {
  const error = errors ? getFormFieldError(name, errors) : null;

  return (
    <Controller
      name={name as any}
      control={control}
      defaultValue={defaultValue || ''}
      rules={rules}
      render={({ field }) => (
        <Tooltip title={tooltip || ''} placement={tooltipPlacement || 'bottom'}>
          <TextField
            className={className}
            {...field}
            label={label}
            multiline={multiline}
            fullWidth={fullWidth}
            required={required}
            variant="outlined"
            helperText={error ? error.message : undefined}
            type={type}
            onBlur={(e) => {
              field.onBlur();
              onBlur?.(e);
            }}
            onChange={(e) => {
              onChange?.(e);
              field.onChange(e);
            }}
            inputProps={inputProps}
            InputProps={{
              ...InputProps,
              readOnly,
              sx: {
                '& input': {
                  cursor: readOnly ? 'default' : 'inherit',
                },
              },
            }}
            error={!!error}
            disabled={disabled}>
            {children}
          </TextField>
        </Tooltip>
      )}
    />
  );
}
